import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import { getTranslation, logError } from "../helpers/utils";
import { getInitialData } from "./InitialDataSlice";

export const getDistrict = createAsyncThunk(
  "DistrictSlice/getDistrict",
  async (data, { rejectWithValue, getState }) => {
    try {
      // Skip if already loaded (raw data exists)
      const state = getState();
      if (state.district.rawDistricts && state.district.rawDistricts.length > 0) {
        return { langId: data.selLanguageId, res: state.district.rawDistricts, fromCache: true };
      }
      const response = await axios.get(
        `${process.env.REACT_APP_LOCAL_API}/districts`
      );
      return { langId: data.selLanguageId, res: response.data.data.Districts };
    } catch (error) {
      logError(error, "DistrictSlice/getDistrict");
      return rejectWithValue(error.response.data);
    }
  }
);

const filterActiveDistricts = (districts) =>
  Array.isArray(districts) ? districts.filter((d) => d.deleted_at == null) : [];

/** Drop selection if the district was soft-deleted or removed from the active list. */
function clearStaleDistrictSelection(state) {
  const sid = state.selDistrictId != null ? String(state.selDistrictId) : "";
  if (!sid) return;
  const inAll = state.allDistricts?.some((d) => String(d._id) === sid);
  const inProv =
    !state.district?.length ||
    state.district.some((d) => String(d._id) === sid);
  if (!inAll || !inProv) {
    state.selDistrictId = "";
  }
}

const processDistricts = (state, districts, langId) => {
  if (!districts) return;
  const active = filterActiveDistricts(districts);
  state.rawDistricts = active; // Store raw for re-translation
  state.allDistricts = active.map((lang) => {
    let transDistrict = { ...lang };
    transDistrict.name = getTranslation(lang.name, langId);
    return transDistrict;
  });
  state.allDistricts = state.allDistricts.sort((a, b) =>
    (a.name || '').localeCompare(b.name || '')
  );
  // Sync district list for current province so modal works from any page
  const ids = (state.provDistrict || []).map((p) =>
    typeof p === "object" && p != null && p._id != null ? p._id : p
  );
  if (state.allDistricts.length && ids.length) {
    state.district = state.allDistricts.filter((d) => ids.includes(d._id));
  } else {
    state.district = [];
  }
  clearStaleDistrictSelection(state);
};

export const DistrictSlice = createSlice({
  name: "district",
  initialState: {
    district: [],
    allDistricts: [],
    rawDistricts: [], // Store raw data for re-translation
    provDistrict: [],
    selDistrictId: "",
  },
  reducers: {
    setDistrictId: (state, action) => {
      state.selDistrictId = action.payload;
    },
    getTranslatedDistricts: (state, action) => {
      const active = filterActiveDistricts(action.payload.districts);
      state.allDistricts = active.map((lang) => {
        let transDistrict = { ...lang };
        transDistrict.name = getTranslation(lang.name, action.payload.langId);
        return transDistrict;
      });
      // Re-sync district list for current province after translation
      const ids = (state.provDistrict || []).map((p) =>
        typeof p === "object" && p != null && p._id != null ? p._id : p
      );
      if (state.allDistricts.length && ids.length) {
        state.district = state.allDistricts.filter((d) => ids.includes(d._id));
      } else {
        state.district = [];
      }
      clearStaleDistrictSelection(state);
    },
    setProvDistrict: (state, action) => {
      state.provDistrict = action.payload;
      // Keep district list in sync so modal works when opened from any page (not only HomePage)
      const ids = Array.isArray(action.payload)
        ? action.payload.map((p) => (typeof p === "object" && p != null && p._id != null ? p._id : p))
        : [];
      if (state.allDistricts?.length && ids.length) {
        state.district = state.allDistricts.filter((d) => ids.includes(d._id));
      } else {
        state.district = [];
      }
      clearStaleDistrictSelection(state);
    },
    setDistrict: (state, action) => {
      state.district = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDistrict.fulfilled, (state, action) => {
        processDistricts(state, action.payload.res, action.payload.langId);
      })
      .addCase(getDistrict.rejected, (state, action) => {
        toast.error(action.payload);
      })
      // Listen for initial data load
      .addCase(getInitialData.fulfilled, (state, action) => {
        if (action.payload?.Districts) {
          state.rawDistricts = filterActiveDistricts(action.payload.Districts);
          // Don't translate yet - wait for language selection
        }
      });
  },
});
export const {
  getTranslatedDistricts,
  setDistrictId,
  setProvDistrict,
  setDistrict,
} = DistrictSlice.actions;

export default DistrictSlice.reducer;
